import React, { useRef, useState } from "react";
import Modal from "react-modal";
import { useInView } from "framer-motion";
import { FaPlay, FaXmark } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { getEmbedUrl, getYouTubeThumbnail } from "../../../utils/helper";
import "./VoiceOfIndustryLeaders.css";

Modal.setAppElement("#root");

/* ------------------------------------------------------------------
   Static data — Industry Leaders / Sponsors & Exhibitors.
   Replace `videoId` with the real YouTube video ID for each entry
   (the part after "v=" in the YouTube URL) when it's available.
   ------------------------------------------------------------------ */
const VIDEOS = [
  {
    id: "il-01",
    name: "Kich Architectural Products",
    role: "Proud Diamond Sponsor",
    association: "Kich",
    title: "Kich Architectural Products at Bharat Buildcon 2026",
    videoId: "bXcWXGjHvvc",
  },
  {
    id: "il-02",
    name: "Xen Bath Fittings",
    role: "Proud Exhibitor",
    association: "Bharat Buildcon — One Nation, One Expo",
    title: "Bharat Buildcon — One Nation, One Expo.",
    videoId: "uWDEJMUoKao",
  },
  {
    id: "il-03",
    name: "Phoenix Metal",
    role: "Proud Partner",
    association: "Bharat Buildcon 2026",
    title:
      "We are proud to have Phoenix Metal as a part of the Bharat Buildcon family",
    videoId: "FlWrj5Tzwwo",
  },
  {
    id: "il-04",
    name: "Punit Patel",
    role: "Industry Leader",
    association: "Bharat Buildcon 2026",
    title: "Mr. Punit Patel on Bharat Buildcon 2026 | One Nation, One Expo",
    videoId: "K5NeXhp1Enk",
  },
  {
    id: "il-05",
    name: "Punit Patel",
    role: "Industry Leader",
    association: "Bharat Buildcon 2026",
    title: "Mr. Punit Patel on Bharat Buildcon 2026 | One Nation, One Expo",
    videoId: "JdkzFZ24rCs",
  },
  {
    id: "il-06",
    name: "Punit Patel",
    role: "Industry Leader",
    association: "Bharat Buildcon 2026",
    title: "Mr. Punit Patel on Bharat Buildcon 2026 | One Nation, One Expo",
    videoId: "",
  },
];

const PARTICLE_COLORS = [
  "var(--hh-saffron-light)",
  "#c9c9c9",
  "var(--hh-green)",
];

const PARTICLES = Array.from({ length: 16 }).map((_, i) => ({
  id: `p-${i}`,
  left: `${(i * 6.3) % 100}%`,
  size: 4 + ((i * 7) % 6),
  duration: 14 + ((i * 5) % 12),
  delay: -(i * 2.1),
  drift: `${(i % 2 === 0 ? 1 : -1) * (18 + (i % 5) * 6)}px`,
  color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
}));

function PlayIcon() {
  return <FaPlay size={16} aria-hidden="true" />;
}

function CloseIcon() {
  return <FaXmark size={18} aria-hidden="true" />;
}

function VideoCard({ video, index, onPlay }) {
  const cardRef = useRef(null);
  const isVisible = useInView(cardRef, { once: true, amount: 0.18 });

  return (
    <article
      ref={cardRef}
      className={`vil-card ${isVisible ? "is-visible" : ""}`}
      style={{ "--vil-delay": `${(index % 3) * 0.09}s` }}
    >
      <div
        className="vil-card__stage"
        role="button"
        tabIndex={0}
        aria-label={`Play video: ${video.title}`}
        onClick={() => onPlay(video)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPlay(video)}
      >
        {video.videoId ? (
          <img
            src={getYouTubeThumbnail(video.videoId)}
            alt={video.title}
            className="vil-card__thumbnail"
          />
        ) : (
          <div className="vil-card__stage-pattern" />
        )}

        <span className="vil-card__play">
          <PlayIcon />
        </span>

        <div className="vil-card__scrim" aria-hidden="true" />
      </div>

      <p className="vil-card__title">{video.title}</p>
    </article>
  );
}

function VideoModal({ video, onClose }) {
  if (!video) return null;

  return (
    <Modal
      isOpen={!!video}
      onRequestClose={onClose}
      className="vil-modal__panel"
      overlayClassName="vil-modal"
      contentLabel={video.title}
    >
      <button
        className="vil-modal__close"
        onClick={onClose}
        aria-label="Close video"
      >
        <CloseIcon />
      </button>
      <div className="vil-modal__frame">
        {video.videoId ? (
          <iframe
            src={getEmbedUrl(video.videoId)}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className="vil-modal__placeholder">
            <PlayIcon />
            <span>Video coming soon</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function VoiceOfIndustryLeaders() {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section className="vil" id="voice-of-industry-leaders">
      <div className="vil__aurora" aria-hidden="true">
        <span className="vil__blob vil__blob--saffron" />
        <span className="vil__blob vil__blob--green" />
        <span className="vil__blob vil__blob--blue" />
      </div>
      <div className="vil__weave" aria-hidden="true" />
      <div className="vil__particles" aria-hidden="true">
        {PARTICLES.map((p) => (
          <span
            key={p.id}
            className="vil__particle"
            style={{
              left: p.left,
              "--size": `${p.size}px`,
              "--dur": `${p.duration}s`,
              "--delay": `${p.delay}s`,
              "--drift": p.drift,
              "--dot-color": p.color,
            }}
          />
        ))}
      </div>

      <header className="vil__header">
        <div className="clean-breadcrumb-wrapper">
          <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="bc-home-link">
              HOME
            </Link>
            <span className="bc-slash">/</span>
            <span className="bc-active-page">VOICE OF INDUSTRY LEADERS</span>
          </nav>
        </div>
        <span className="vil__eyebrow">
          <span className="vil__tricolor-dots">
            <span />
            <span />
            <span />
          </span>
          Voices from Bharat Buildcon 2026
        </span>
        <h2 className="vil__title">
          Voice of <span className="vil__title-accent">Industry Leaders</span>
        </h2>
        <p className="vil__subtitle">
          Hear directly from the sponsors and exhibitors powering India&rsquo;s
          journey to becoming a global building-materials hub &mdash; One
          Nation, One Expo.
        </p>
      </header>

      <div className="vil__grid">
        {VIDEOS.map((video, i) => (
          <VideoCard
            key={video.id}
            video={video}
            index={i}
            onPlay={setActiveVideo}
          />
        ))}
      </div>

      {activeVideo && (
        <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </section>
  );
}
